import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { GraduationCap, Award, Wallet, MessageCircle, CalendarCheck } from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../lib/motion";
import { Catalogo } from "../components/Catalogo";
import { LAUREE, MASTER, DATA_VERIFICA_LAUREE, DATA_VERIFICA_MASTER } from "../data/catalogo";
import { LAUREE_PARTNERS } from "../data/content";
import { SITE } from "../config/site";

const WA_GENERIC = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(
  "Buongiorno, vorrei informazioni sui corsi di laurea e i master eCampus e Link."
)}`;

export default function Lauree() {
  usePageMeta(
    "Università",
    "Catalogo completo dei corsi di laurea e dei master universitari eCampus e Link: Centro Studi Forma Futuro ti accompagna nella scelta e nell'iscrizione."
  );
  const [tab, setTab] = useState("lauree");

  return (
    <main>
      <PageHeader
        testId="page-universita"
        eyebrow="Università · eCampus e Link"
        title="Il tuo futuro, il percorso giusto."
        description="Scopri i corsi di laurea e i master universitari eCampus e Link. Centro Studi Forma Futuro ti accompagna nella scelta del percorso e nelle procedure di iscrizione."
      />

      {/* Partner */}
      <section className="pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {LAUREE_PARTNERS.map((p) => (
              <Reveal key={p.nome} className="bg-white rounded-[1.4rem] border border-slate-200 px-7 py-6 flex items-center gap-6">
                <img src={p.logo} alt={`Logo ${p.nome}`} className="h-10 w-auto object-contain shrink-0" />
                <p className="text-sm text-slate-600 leading-relaxed">{p.descrizione}</p>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-3 rounded-[1.2rem] bg-azure-light px-6 py-4">
            <span className="inline-flex items-center gap-2.5 text-sm font-bold text-navy">
              <Wallet size={17} className="text-azure" />
              Possibilità di pagamento a rate.
            </span>
            <span className="text-[13px] text-slate-600">
              Modalità e condizioni da verificare per il percorso scelto.
            </span>
          </Reveal>
        </div>
      </section>

      {/* Tabs + cataloghi */}
      <section className="pb-20 lg:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex justify-center mb-10">
            <div data-testid="universita-tabs" className="inline-flex rounded-full bg-white border border-slate-200 p-1.5 shadow-sm">
              <button
                data-testid="tab-lauree"
                onClick={() => setTab("lauree")}
                className={`inline-flex items-center gap-2.5 rounded-full px-6 sm:px-8 py-3 text-sm font-bold transition-all duration-300 ${
                  tab === "lauree" ? "bg-navy text-white shadow" : "text-navy/60 hover:text-navy"
                }`}
              >
                <GraduationCap size={17} />
                Corsi di laurea
              </button>
              <button
                data-testid="tab-master"
                onClick={() => setTab("master")}
                className={`inline-flex items-center gap-2.5 rounded-full px-6 sm:px-8 py-3 text-sm font-bold transition-all duration-300 ${
                  tab === "master" ? "bg-navy text-white shadow" : "text-navy/60 hover:text-navy"
                }`}
              >
                <Award size={17} />
                Master universitari
              </button>
            </div>
          </div>

          {tab === "lauree" ? (
            <motion.div key="lauree" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
              <p className="text-center text-xs text-slate-400 mb-6">
                Offerta formativa 2026/2027 — dati verificati sulle fonti ufficiali il {DATA_VERIFICA_LAUREE}
              </p>
              <Catalogo items={LAUREE} kind="laurea" testId="catalogo-lauree" />
            </motion.div>
          ) : (
            <motion.div key="master" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
              <p className="text-center text-xs text-slate-400 mb-6">
                Master di I e II livello — dati verificati sulle fonti ufficiali il {DATA_VERIFICA_MASTER}.
                Edizioni, scadenze e bandi sono sempre quelli pubblicati sulle pagine ufficiali.
              </p>
              <Catalogo items={MASTER} kind="master" testId="catalogo-master" />
            </motion.div>
          )}
        </div>
      </section>

      {/* Sezione finale */}
      <section data-testid="universita-cta-finale" className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <Reveal className="relative bg-navy rounded-[2rem] px-8 py-14 sm:px-14 lg:px-20 lg:py-20 overflow-hidden grain">
            <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full border border-dashed border-sky-400/30 spin-slower" />
            <div className="absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-azure-veil blur-3xl" />
            <div className="relative max-w-2xl">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white text-balance">
                Non sai quale percorso scegliere?
              </h2>
              <p className="mt-5 text-base md:text-lg text-white/65 leading-relaxed">
                Partiamo dai tuoi obiettivi e dal tuo percorso di studi. Contatta
                Centro Studi Forma Futuro per ricevere informazioni e richiedere
                un appuntamento di orientamento.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <a
                  href={WA_GENERIC}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="universita-cta-whatsapp"
                  className="inline-flex items-center justify-center gap-2.5 rounded-full bg-[#25D366] px-8 py-4 text-sm font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:brightness-95"
                >
                  <MessageCircle size={17} />
                  Scrivici su WhatsApp
                </a>
                <Link
                  to="/contatti"
                  data-testid="universita-cta-appuntamento"
                  className="inline-flex items-center justify-center gap-2.5 rounded-full bg-white px-8 py-4 text-sm font-bold text-navy transition-all duration-300 hover:-translate-y-0.5 hover:bg-sky-300"
                >
                  <CalendarCheck size={17} />
                  Richiedi un appuntamento
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
